import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { LOGO_URL } from "@/lib/brand";
import {
  LayoutDashboard, ShoppingBag, Users, Settings, Receipt, Package, Image as ImageIcon,
  LogOut, ExternalLink, ChevronLeft, Boxes, Truck, SlidersHorizontal, UserCircle, BarChart3,
  MessageCircle, Sparkles, FileBarChart, FileEdit
} from "lucide-react";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "analytics.view", end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, perm: "orders.view" },
  { to: "/admin/layout-requests", label: "Layout Requests", icon: FileEdit, perm: "orders.view", liveBadge: "layout" },
  { to: "/admin/chat-inbox", label: "Chat inbox", icon: MessageCircle, perm: "orders.view", liveBadge: "chat" },
  { to: "/admin/ai-designer", label: "AI Designer", icon: Sparkles, perm: "orders.view" },
  { to: "/admin/ai-logs", label: "Customer AI Logs", icon: FileBarChart, perm: "analytics.view" },
  { to: "/admin/printservices", label: "Print Services", icon: ImageIcon, perm: "services.manage" },
  { to: "/admin/customers", label: "Customers", icon: UserCircle, perm: "customers.view" },
  { to: "/admin/users", label: "Users", icon: Users, perm: "users.manage" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, perm: "analytics.view" },
  { to: "/admin/expenses", label: "Expenses", icon: Receipt, perm: "expenses.manage" },
  { to: "/admin/supplies", label: "Supplies", icon: Boxes, perm: "supplies.manage" },
  { to: "/admin/suppliers", label: "Suppliers", icon: Truck, perm: "supplies.manage" },
  { to: "/admin/pricing", label: "Pricing config", icon: Settings, perm: "pricing.manage" },
  { to: "/admin/settings", label: "Settings", icon: SlidersHorizontal, perm: "pricing.manage" },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [layoutUnread, setLayoutUnread] = useState(0);

  // Poll the admin chat unread count + layout-requests unprocessed for sidebar badges
  useEffect(() => {
    if (!user) return undefined;
    const load = async () => {
      try {
        const apiMod = await import("@/lib/api");
        const c = await apiMod.api.get("/admin/chat/unread-count");
        setChatUnread(c.data?.unread || 0);
        const l = await apiMod.api.get("/admin/layout-requests/unprocessed-count");
        setLayoutUnread(l.data?.unprocessed || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const accessible = NAV.filter((n) => hasPerm(user, n.perm));

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-layout">
      <aside className={`bg-black text-neutral-200 ${collapsed ? "w-20" : "w-64"} transition-all duration-200 flex flex-col`}>
        <div className="h-20 flex items-center gap-3 px-4 border-b border-neutral-900">
          <img src={LOGO_URL} alt="" className="h-12 w-12 rounded-lg object-contain bg-black ring-1 ring-white/10 shrink-0" />
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-display font-extrabold text-sm tracking-tight text-white">PRINT AD VENTURE</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">Admin Portal</div>
            </div>
          )}
        </div>

        <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
          {accessible.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-neutral-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="font-medium flex-1">{n.label}</span>}
              {n.liveBadge === "chat" && chatUnread > 0 && (
                <span data-testid="sidebar-chat-badge"
                  className={`${collapsed ? "absolute -top-1 -right-1" : ""} min-w-[18px] h-[18px] px-1 rounded-full bg-[#06B6D4] text-[10px] font-bold text-white flex items-center justify-center`}>
                  {chatUnread > 9 ? "9+" : chatUnread}
                </span>
              )}
              {n.liveBadge === "layout" && layoutUnread > 0 && (
                <span data-testid="sidebar-layout-badge"
                  className={`${collapsed ? "absolute -top-1 -right-1" : ""} min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-white flex items-center justify-center`}>
                  {layoutUnread > 9 ? "9+" : layoutUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-neutral-900 space-y-2">
          {!collapsed && (
            <div className="px-3 py-2 text-xs">
              <p className="text-neutral-500 uppercase tracking-wider">Signed in</p>
              <p className="text-white font-semibold truncate mt-0.5">{user?.name}</p>
              <p className="text-[#06B6D4] text-[11px]">{user?.role?.replace("_", " ")}</p>
            </div>
          )}
          <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-neutral-400 hover:bg-white/5 hover:text-white">
            <ExternalLink className="h-4 w-4" /> {!collapsed && "View website"}
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-neutral-400 hover:bg-white/5 hover:text-white" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> {!collapsed && "Sign out"}
          </button>
          <button onClick={() => setCollapsed(!collapsed)} className="w-full flex items-center justify-center px-3 py-2 rounded-lg text-xs text-neutral-500 hover:bg-white/5">
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
